// Copyright 2026 Granite (granite.md). All rights reserved.
// Proprietary and confidential. Unauthorized copying is prohibited.

import { setUiText } from "@granite/ui";

import { en, type MessageKey } from "./messages.en.js";

export type { MessageKey };
export type Locale = "en";

/** Translations by locale. English is complete; others may be partial and fall back. */
const catalogs: Record<Locale, Partial<Record<MessageKey, string>>> = { en };

let current: Locale = "en";

export function setLocale(locale: Locale): void {
  current = locale;
  document.documentElement.lang = locale;
  shareUiText();
}

/** The words the shared components show by themselves (the dialogs' close button). */
function shareUiText(): void {
  setUiText({ close: t("panels.dialog.close") });
}

export function isMessageKey(key: string): key is MessageKey {
  return Object.hasOwn(en, key);
}

/** Returns the UI string for key in the current locale, with {placeholders} filled in. */
export function t(key: MessageKey, vars?: Record<string, string | number>): string {
  const template = catalogs[current][key] ?? en[key];
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (match, name: string) => {
    const value = vars[name];
    return value === undefined ? match : String(value);
  });
}

/**
 * Picks the singular or plural message for count by the current locale's rules, with {count}
 * and any other placeholders filled in: plural(n, "files.one", "files.other").
 */
export function plural(count: number, one: MessageKey, other: MessageKey, vars?: Record<string, string | number>): string {
  const key = new Intl.PluralRules(current).select(count) === "one" ? one : other;
  return t(key, { count, ...vars });
}

/** Formats a date for display in the current locale. */
export function formatDate(iso: string): string {
  return new Intl.DateTimeFormat(current, { dateStyle: "medium" }).format(new Date(iso));
}

shareUiText();
